import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import HubIcon from '@mui/icons-material/Hub';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TableChartIcon from '@mui/icons-material/TableChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box, Typography, Container, Paper, Tooltip, IconButton, Button,
    CircularProgress, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Collapse, Stack, Divider, Slider, Alert, AlertTitle
} from '@mui/material';
import {
    ContentCopy as ContentCopyIcon, Refresh as RefreshIcon,
    NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon,
    FirstPage as FirstPageIcon, LastPage as LastPageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../../utils/constants';
import { StatCard } from '../../../../Pages/Analytics.page';

// Helper function to truncate addresses for display
const truncateAddress = (address, length = 10) => {
    if (!address) return '';
    return `${ address.substring(0, length) }...${ address.substring(address.length - length) }`;
};
const API_BASE_URI = API_BASE_URL + '/users';

// Row component for expandable transactions
function ConnectionTableRow({ row }) {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    return (
        <>
            <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: '#2A2A2A' }, '&:hover': { bgcolor: '#3A3A3A' } }}>
                <TableCell sx={{ color: '#E0E0E0' }}>
                    <Tooltip title={row.sharedSenderAddress}><Typography variant="body2">{truncateAddress(row.sharedSenderAddress)}</Typography></Tooltip>
                </TableCell>
                <TableCell sx={{ color: '#E0E0E0' }} align="right">{row.numberOfUsers}</TableCell>
                <TableCell sx={{ color: '#E0E0E0' }}>
                    <IconButton aria-label={t('connections.expandRow')} size="small" onClick={() => setOpen(!open)} sx={{ color: '#E0E0E0' }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    <Typography variant="body2" component="span">{row.transactions.length} {t('connections.transactions')}</Typography>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, bgcolor: '#3A3A3A', p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ color: '#E0E0E0' }}>{t('connections.transactions')}</Typography>
                            <Table size="small">
                                <TableHead><TableRow><TableCell sx={{ color: '#E0E0E0', fontWeight: 'bold' }}>{t('connections.txId')}</TableCell><TableCell sx={{ color: '#E0E0E0', fontWeight: 'bold' }}>{t('connections.user')}</TableCell><TableCell align="right" sx={{ color: '#E0E0E0', fontWeight: 'bold' }}>{t('connections.amountBtc')}</TableCell><TableCell align="right" sx={{ color: '#E0E0E0', fontWeight: 'bold' }}>{t('connections.time')}</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {row.transactions.sort((a, b) => b.time - a.time).map((transaction, i) => (
                                        <TableRow key={i}>
                                            <TableCell component="th" scope="row" sx={{ color: '#E0E0E0' }}>
                                                <Tooltip title={transaction.txId}><a href={`https://mempool.space/tx/${ transaction.txId }`} target="_blank" rel="noopener noreferrer" style={{ color: '#E0E0E0', textDecoration: 'none' }}>{truncateAddress(transaction.txId)}</a></Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ color: '#E0E0E0' }}><Tooltip title={transaction.user}><Typography variant="body2">{truncateAddress(transaction.user)}</Typography></Tooltip></TableCell>
                                            <TableCell align="right" sx={{ color: '#E0E0E0' }}>{(transaction.amount / 1e11).toFixed(8)}</TableCell>
                                            <TableCell align="right" sx={{ color: '#E0E0E0' }}>{new Date(transaction.time).toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

const App = ({ userId }) => {
    const svgRef = useRef();
    const containerRef = useRef();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [tooltipContent, setTooltipContent] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [currentUserId, setCurrentUserId] = useState(userId);
    const [fullTransactionData, setFullTransactionData] = useState([]);
    const [connectionTableData, setConnectionTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showTable, setShowTable] = useState('graph');

    const [stats, setStats] = useState({ totalConnectedUsers: 0, totalConnectedDepositAddresses: 0, totalWithdraws: 0, usersTransactions: [] });

    const [windowSize, setWindowSize] = useState(50);
    const [neighborPageSize, setNeighborPageSize] = useState(50);
    const [currentWindow, setCurrentWindow] = useState(0);

    const userNodeColor = '#00CED1';
    const receiverNodeColor = '#FF6347';
    const userSenderLinkColor = '#3CB371';
    const specialUserNodeColor = '#FFD700';

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const fetchConnections = useCallback(async (userId) => {
        setIsLoading(true);
        setError(null);
        setFullTransactionData([]);
        setConnectionTableData([]);
        try {
            const response = await fetch(`${ API_BASE_URI }/${ userId }/connections/withdraws`);
            if (!response.ok) throw new Error(`HTTP error! status: ${ response.status }`);
            const data = await response.json();

            setConnectionTableData(data.connections);
            let txData = [...data.userTransactions.map(tx => ({ ...tx, amount: tx.amount / 1e11 })), ...data.connections.flatMap(c => c.transactions.map(tx => ({ ...tx, amount: tx.amount / 1e11 })))];
            const uniqueTx = Array.from(new Map(txData.map(tx => [tx.txId, tx])).values());
            setFullTransactionData(uniqueTx);

            let userUniqueSenders = new Set(data.userTransactions.map(tx => tx.withdrawAddress));
            setStats({
                totalConnectedUsers: new Set(uniqueTx.map(tx => tx.user)).size,
                totalConnectedDepositAddresses: userUniqueSenders.size,
                totalWithdraws: data.userTransactions.length || 0,
                usersTransactions: data.userTransactions?.map(tx => ({ ...tx, amount: tx.amount / 1e11 })) || [],
            });
        } catch (e) {
            console.error("Failed to fetch connections:", e);
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchConnections(currentUserId); }, [currentUserId, fetchConnections]);

    const fullGraph = useMemo(() => {
        const userNodes = new Map();
        const receiverNodes = new Map();
        const linkMap = new Map();

        fullTransactionData.forEach(withdraw => {
            if (!userNodes.has(withdraw.user)) userNodes.set(withdraw.user, { id: withdraw.user, label: truncateAddress(withdraw.user), type: 'user' });
            const receiverAddr = withdraw.withdrawAddress;
            if (!receiverNodes.has(receiverAddr)) receiverNodes.set(receiverAddr, { id: receiverAddr, label: truncateAddress(receiverAddr), fullAddress: receiverAddr, type: 'receiver' });
            const key = `${ withdraw.user }|${ receiverAddr }`;
            if (!linkMap.has(key)) linkMap.set(key, { source: withdraw.user, target: receiverAddr, type: 'withdraw', amount: 0 });
            linkMap.get(key).amount += withdraw.amount;
        });

        const nodes = [...userNodes.values(), ...receiverNodes.values()];
        const links = Array.from(linkMap.values()).map((link, idx) => ({ ...link, id: `link-${ idx }` }));
        return { nodes, links };
    }, [fullTransactionData]);

    const explorationPlan = useMemo(() => {
        if (!fullGraph?.nodes?.length) return [];
        const plan = [];
        const { nodes, links } = fullGraph;
        const neighborsOf = new Map();
        nodes.forEach(n => neighborsOf.set(n.id, []));
        links.forEach(link => {
            const sourceId = link.source.id || link.source; const targetId = link.target.id || link.target;
            neighborsOf.get(sourceId)?.push(targetId); neighborsOf.get(targetId)?.push(sourceId);
        });
        const processedAsHub = new Set();
        for (const node of nodes) {
            const neighbors = Array.from(new Set(neighborsOf.get(node.id) || []));
            if (neighbors.length > neighborPageSize) {
                processedAsHub.add(node.id);
                for (let i = 0; i < neighbors.length; i += neighborPageSize) {
                    const neighborSlice = neighbors.slice(i, i + neighborPageSize);
                    const nodeIdsInView = new Set([node.id, ...neighborSlice]);
                    plan.push({ title: `${ truncateAddress(node.id) } (Neighbors ${ i + 1 }-${ i + neighborSlice.length })`, nodeIds: nodeIdsInView });
                }
            }
        }
        const remainingNodes = nodes.filter(n => !processedAsHub.has(n.id));
        for (let i = 0; i < remainingNodes.length; i += windowSize) {
            const nodeSlice = remainingNodes.slice(i, i + windowSize);
            const nodeIdsInView = new Set(nodeSlice.map(n => n.id));
            nodeSlice.forEach(n => { (neighborsOf.get(n.id) || []).forEach(neighborId => nodeIdsInView.add(neighborId)); });
            const endRange = Math.min(i + windowSize, remainingNodes.length);
            plan.push({ title: `Nodes ${ i + 1 }-${ endRange } & Their Neighbors`, nodeIds: nodeIdsInView });
        }
        return plan;
    }, [fullGraph, windowSize, neighborPageSize]);

    const windowedData = useMemo(() => {
        const totalWindows = explorationPlan.length;
        if (totalWindows === 0) return { currentNodes: [], currentLinks: [], totalWindows: 0, title: 'No Data', stats: { total: 0, showing: 0, window: 0, links: 0 } };
        const currentPlanItem = explorationPlan[Math.min(currentWindow, totalWindows - 1)];
        const { nodeIds, title } = currentPlanItem;
        const allNodesById = new Map(fullGraph.nodes.map(node => [node.id, node]));
        const currentNodes = Array.from(nodeIds).map(id => allNodesById.get(id)).filter(Boolean);
        const currentLinks = fullGraph.links.filter(link => nodeIds.has(link.source.id || link.source) && nodeIds.has(link.target.id || link.target));
        return { currentNodes, currentLinks, totalWindows, title, stats: { total: fullGraph.nodes.length, showing: currentNodes.length, window: currentWindow + 1, links: currentLinks.length } };
    }, [fullGraph, explorationPlan, currentWindow]);

    const navigateWindow = useCallback((direction) => { setCurrentWindow(prev => { switch (direction) { case 'first': return 0; case 'prev': return Math.max(0, prev - 1); case 'next': return Math.min(windowedData.totalWindows - 1, prev + 1); case 'last': return windowedData.totalWindows - 1; default: return prev; } }); }, [windowedData.totalWindows]);
    useEffect(() => { setCurrentWindow(0); }, [fullGraph, windowSize, neighborPageSize]);

    useEffect(() => {
        const updateDimensions = () => { if (containerRef.current) setDimensions({ width: containerRef.current.offsetWidth, height: containerRef.current.offsetHeight }); };
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    useEffect(() => {
        if (showTable !== 'graph' || dimensions.width === 0 || !windowedData.currentNodes.length) {
            d3.select(svgRef.current).selectAll('*').remove();
            return;
        }
        const { currentNodes, currentLinks } = windowedData;
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        const g = svg.append('g');
        const linkGroup = g.append('g').attr('class', 'links');
        const nodeGroup = g.append('g').attr('class', 'nodes');

        const simulation = d3.forceSimulation(currentNodes)
            .force('link', d3.forceLink(currentLinks).id(d => d.id).distance(150))
            .force('charge', d3.forceManyBody().strength(-600))
            .force('center', d3.forceCenter(dimensions.width / 2, dimensions.height / 2))
            .force('collision', d3.forceCollide().radius(d => d.type === 'user' ? 40 : 25));

        const link = linkGroup.selectAll('line').data(currentLinks).join('line').attr('stroke', userSenderLinkColor).attr('stroke-opacity', 0.6).attr('stroke-width', 1);
        const linkText = linkGroup.selectAll('text').data(currentLinks).join('text').attr('font-size', '8px').attr('fill', '#AAA').attr('text-anchor', 'middle').text(d => `${ d.amount.toFixed(4) } BTC`);
        const node = nodeGroup.selectAll('g').data(currentNodes).join('g').call(d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended));

        node.append('circle')
            .attr('r', d => d.id === currentUserId ? 25 : (d.type === 'user' ? 20 : 12))
            .attr('fill', d => d.id === currentUserId ? specialUserNodeColor : (d.type === 'user' ? userNodeColor : receiverNodeColor));

        node.append('text').attr('x', d => d.id === currentUserId ? 30 : (d.type === 'user' ? 25 : 15)).attr('y', '0.31em').attr('font-size', d => d.type === 'user' ? '12px' : '10px').attr('fill', '#EEE').text(d => d.label);

        node.on('mouseover', (event, d) => { setTooltipContent(d); setTooltipPosition({ x: event.pageX + 10, y: event.pageY + 10 }); }).on('mouseout', () => setTooltipContent(null)).on('click', (event, d) => { if (d.type === 'user' && d.id !== currentUserId) setCurrentUserId(d.id); });

        simulation.on('tick', () => {
            link.attr('x1', d => d.source.x).attr('y1', d => d.source.y).attr('x2', d => d.target.x).attr('y2', d => d.target.y);
            node.attr('transform', d => `translate(${ d.x },${ d.y })`);
            linkText.attr('x', d => (d.source.x + d.target.x) / 2).attr('y', d => (d.source.y + d.target.y) / 2);
        });

        function dragstarted(event) { if (!event.active) simulation.alphaTarget(0.3).restart(); event.subject.fx = event.subject.x; event.subject.fy = event.subject.y; }
        function dragged(event) { event.subject.fx = event.x; event.subject.fy = event.y; }
        function dragended(event) { if (!event.active) simulation.alphaTarget(0); }

        const zoom = d3.zoom().scaleExtent([0.5, 5]).on('zoom', (event) => g.attr('transform', event.transform));
        svg.call(zoom);

        return () => simulation.stop();
    }, [showTable, dimensions, windowedData, currentUserId]);

    const handleReset = () => { setCurrentUserId(userId); setShowTable('graph'); };
    const toggleTableVisibility = (tableType) => { setShowTable(prev => prev === tableType ? 'graph' : tableType); };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', color: '#E0E0E0' }}>
            <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}><StatCard title={t('connections.withdraws')} value={stats.totalWithdraws} color={'#F48600'} icon={<ArrowUpwardIcon />} /></Grid>
                <Grid item xs={6} md={3}><StatCard title={t('connections.connectedUsers')} value={stats.totalConnectedUsers} icon={<HubIcon />} color={"#22d3ee"} /></Grid>
                <Grid item xs={6} md={3}><StatCard title={t('connections.addresses')} value={stats.totalConnectedDepositAddresses} icon={<PeopleAltIcon />} color={"#ffc107"} /></Grid>
                <Grid item xs={6} md={3}><Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                    <Button variant="contained" onClick={() => toggleTableVisibility('connections')} startIcon={<TableChartIcon />}>{showTable === 'connections' ? t('connections.graphView') : t('connections.tableView')}</Button>
                    <Button variant="contained" onClick={() => toggleTableVisibility('userTransactions')} startIcon={<TableChartIcon />}>{showTable === 'userTransactions' ? t('connections.hideUserTransactions') : t('connections.userTransactionsTable')}</Button>
                </Stack></Grid>
            </Grid>

            <Alert
                severity="info"
                sx={{ mb: 2, bgcolor: '#273142', color: '#a6c5e4' }}
                action={
                    <Button
                        color="primary"
                        variant="contained"
                        size="small"
                        onClick={() => window.open('/explorer/explore')}
                    >
                        {t('connections.goToFlokiScan')}
                    </Button>
                }
            >
                <AlertTitle sx={{ color: '#64b5f6' }}>{t('connections.proTip')}</AlertTitle>
                {t('connections.useFlokiScanTip')}
            </Alert>

            {showTable === 'graph' && (
                <Paper elevation={2} sx={{ p: 1.5, mb: 2, bgcolor: '#2A2A2A', display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: specialUserNodeColor, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#E0E0E0' }}>{t('connections.currentUser')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: userNodeColor, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#E0E0E0' }}>{t('connections.otherUser')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: receiverNodeColor, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#E0E0E0' }}>{t('connections.receiverAddress')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 24, height: 2, bgcolor: userSenderLinkColor, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#E0E0E0' }}>{t('connections.withdrawLink')}</Typography>
                    </Box>
                </Paper>
            )}

            <Paper elevation={3} sx={{ flexGrow: 1, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#1A1A1A', border: '1px solid #333' }}>
                <Box ref={containerRef} sx={{ flexGrow: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {isLoading && <CircularProgress sx={{ color: '#FF7F00' }} />}
                    {error && <Typography color="error">{t('connections.error')}: {error}</Typography>}
                    {!isLoading && !error && fullGraph.nodes.length === 0 && showTable === 'graph' && (<Typography color="text.secondary">{t('connections.noConnectionData')}</Typography>)}
                    {!isLoading && !error && showTable === 'graph' && fullGraph.nodes.length > 0 && (<svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>)}

                    {!isLoading && !error && showTable === 'connections' && (<TableContainer component={Box} sx={{ maxHeight: '100%', width: '100%', bgcolor: '#1A1A1A' }}>
                        <Table stickyHeader><TableHead><TableRow sx={{ '& th': { bgcolor: '#222', color: '#E0E0E0', fontWeight: 'bold' } }}><TableCell>{t('connections.sharedReceiverAddress')}</TableCell><TableCell align="right">{t('connections.numberOfUsers')}</TableCell><TableCell>{t('connections.transactions')}</TableCell></TableRow></TableHead>
                            <TableBody>{connectionTableData.map((connection, index) => (<ConnectionTableRow key={index} row={connection} />))}</TableBody>
                        </Table>
                    </TableContainer>)}

                    {!isLoading && !error && showTable === 'userTransactions' && (<TableContainer component={Box} sx={{ maxHeight: '100%', width: '100%', bgcolor: '#1A1A1A' }}>
                        <Table stickyHeader><TableHead><TableRow sx={{ '& th': { bgcolor: '#222', color: '#E0E0E0', fontWeight: 'bold' } }}><TableCell>{t('connections.txId')}</TableCell><TableCell>{t('connections.user')}</TableCell><TableCell>{t('connections.receiverAddresses')}</TableCell><TableCell align="right">{t('connections.amountBtc')}</TableCell><TableCell align="right">{t('connections.time')}</TableCell></TableRow></TableHead>
                            <TableBody>{stats.usersTransactions.sort((a, b) => b.time - a.time).map((transaction, index) => (
                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#2A2A2A' }, '&:hover': { bgcolor: '#3A3A3A' } }}>
                                    <TableCell sx={{ color: '#E0E0E0' }}><Tooltip title={transaction.txId}><a href={`https://mempool.space/tx/${ transaction.txId }`} target="_blank" rel="noopener noreferrer" style={{ color: '#E0E0E0' }}>{truncateAddress(transaction.txId)}</a></Tooltip></TableCell>
                                    <TableCell sx={{ color: '#E0E0E0' }}><Tooltip title={transaction.user}><Typography variant="body2">{truncateAddress(transaction.user)}</Typography></Tooltip></TableCell>
                                    <TableCell sx={{ color: '#E0E0E0' }}><Tooltip title={transaction.withdrawAddress}><Typography variant="body2">{truncateAddress(transaction.withdrawAddress)}</Typography></Tooltip></TableCell>
                                    <TableCell align="right" sx={{ color: '#E0E0E0' }}>{transaction.amount.toFixed(8)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#E0E0E0' }}>{new Date(transaction.time).toLocaleString()}</TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </TableContainer>)}
                </Box>

                {showTable === 'graph' && (
                    <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, boxShadow: 1, bgcolor: '#2A2A2A', borderTop: '1px solid #333' }}>
                        <IconButton size="small" onClick={() => navigateWindow('first')} disabled={currentWindow === 0}><FirstPageIcon /></IconButton>
                        <IconButton size="small" onClick={() => navigateWindow('prev')} disabled={currentWindow === 0}><NavigateBeforeIcon /></IconButton>
                        <Box sx={{ minWidth: 200, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{windowedData.title || t('connections.graphView')}</Typography>
                            <Typography variant="caption" display="block">{t('connections.window')}: {windowedData.stats.window || 1}/{windowedData.totalWindows || 1}</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => navigateWindow('next')} disabled={currentWindow >= windowedData.totalWindows - 1}><NavigateNextIcon /></IconButton>
                        <IconButton size="small" onClick={() => navigateWindow('last')} disabled={currentWindow >= windowedData.totalWindows - 1}><LastPageIcon /></IconButton>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Typography variant="body2">{t('connections.nodes')}: {windowedData.stats.showing}/{windowedData.stats.total}</Typography>
                        <Typography variant="body2" sx={{ ml: 2 }}>{t('connections.links')}: {windowedData.currentLinks.length}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="body2">{t('connections.hubSize')}:</Typography><Slider value={neighborPageSize} onChange={(_, value) => setNeighborPageSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" /></Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}><Typography variant="body2">{t('connections.groupSize')}:</Typography><Slider value={windowSize} onChange={(_, value) => setWindowSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" /></Box>
                        <Button variant="contained" onClick={handleReset} startIcon={<RefreshIcon />} sx={{ ml: 2 }}>{t('connections.reset')}</Button>
                    </Paper>
                )}
            </Paper>
            {tooltipContent && (
                <Paper sx={{ position: 'absolute', left: tooltipPosition.x, top: tooltipPosition.y, p: 1, bgcolor: 'rgba(0,0,0,0.8)', color: '#E0E0E0', borderRadius: 1, pointerEvents: 'none', zIndex: 1000, border: '1px solid #FF7F00' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{tooltipContent.type === 'user' ? t('connections.userId') : t('connections.receiverAddressLabel')}</Typography>
                    <Typography variant="caption">{tooltipContent.fullAddress || tooltipContent.id}</Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(tooltipContent.fullAddress || tooltipContent.id)} sx={{ color: '#E0E0E0', ml: 0.5, pointerEvents: 'auto' }}><ContentCopyIcon fontSize="small" /></IconButton>
                </Paper>
            )}
        </Container>
    );
};

export default App;